import React, { useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import i18n from 'i18n-js';
import { Button, makeStyles, Paper, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';

import { useAppSelector } from 'index';
import { theme } from 'config/theme';
import tableMapsStore from 'store/tableMapsStore';
import TableMapPreview from 'components/tableMaps/CreateTableMapWizard/components/TableMapPreview';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import TableMapDetailsSkeleton from './TableMapDetailsSkeleton';

const useStyles = makeStyles({
    root: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        maxHeight: '100vh',
        overflowY: 'auto',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.common.white,
        padding: 24,
        paddingBottom: 40,
        gap: 20,
        // Sections must overflow and scroll: one with `overflow: hidden` would otherwise shrink to nothing.
        '& > *': {
            flexShrink: 0,
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap' as const,
        gap: 12,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap' as const,
    },
    title: {
        fontSize: 22,
        fontWeight: 700,
        color: theme.palette.primary.dark,
        lineHeight: 1.2,
    },
    statusPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        borderRadius: 16,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.3,
    },
    statusDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
    },
    editButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        letterSpacing: 1.25,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    metricsRow: {
        display: 'flex',
        gap: 16,
    },
    metricCard: {
        flex: '1 1 0',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        padding: '18px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    metricLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    metricValue: {
        fontSize: 28,
        fontWeight: 700,
        color: theme.palette.primary.dark,
        lineHeight: 1.2,
    },
    sectionLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        marginBottom: 10,
    },
    previewWrapper: {
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    // The wizard's canvas height (98% Container minus its header, body padding and toolbar slot), so both draw the map alike.
    previewCanvas: {
        height: 'calc(98vh - 337px)',
        minHeight: 420,
    },
    summaryCard: {
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    summaryCardHeader: {
        padding: '14px 16px 10px',
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
    },
    summaryRows: {
        padding: '8px 0',
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    summaryLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    summaryValue: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
        textAlign: 'right' as const,
    },
    centered: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        height: '100%',
    },
    notFoundText: {
        fontSize: 15,
        color: theme.palette.text.secondary,
    },
});

function formatPublishedAt(iso: string): string {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${datePart} - ${timePart}`;
}

export default function TableMapDetailsPage() {
    const classes = useStyles();
    const { id } = useParams<{ id: string }>();
    const dispatch = useThunkDispatch();
    const history = useHistory();

    const details = useAppSelector((state) => state.tableMaps.details[id]);
    const [loadedId, setLoadedId] = useState<string | null>(null);

    useEffect(() => {
        dispatch(tableMapsStore.actionCreators.getTableMapDetails(id)).finally(() =>
            setLoadedId(id)
        );
    }, [id]);

    const isSkeletonLoading = loadedId !== id && !details;
    const skeleton = <TableMapDetailsSkeleton key={id} isLoading={isSkeletonLoading} />;

    if (isSkeletonLoading) {
        return <div className={classes.root}>{skeleton}</div>;
    }

    if (!details) {
        return (
            <div className={classes.centered}>
                <Typography className={classes.notFoundText}>
                    {i18n.t('tableMaps.details.notFound')}
                </Typography>
            </div>
        );
    }

    if (details.isDraft) {
        return <Redirect to={`/table-maps/${id}/edit`} />;
    }

    const totalSeats = details.tables.reduce((sum, t) => sum + t.seats, 0);
    const activeTables = details.tables.filter((t) => t.isActive).length;
    const isActive = details.isActive;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.headerLeft}>
                    <Typography className={classes.title}>{details.name}</Typography>
                    <span
                        className={classes.statusPill}
                        style={{
                            backgroundColor: isActive
                                ? theme.palette.success.light
                                : theme.palette.secondary.light,
                            color: isActive
                                ? theme.palette.success.main
                                : theme.palette.text.secondary,
                        }}
                    >
                        <span
                            className={classes.statusDot}
                            style={{
                                backgroundColor: isActive
                                    ? theme.palette.success.main
                                    : theme.palette.secondary.main,
                            }}
                        />
                        {isActive
                            ? i18n.t('tableMaps.statusActive')
                            : i18n.t('tableMaps.statusInactive')}
                    </span>
                </div>
                <Button
                    variant="contained"
                    className={classes.editButton}
                    startIcon={<EditIcon />}
                    onClick={() => history.push(`/table-maps/${id}/edit`)}
                >
                    {i18n.t('tableMaps.details.editTableMap')}
                </Button>
            </div>

            <div className={classes.metricsRow}>
                <Paper className={classes.metricCard} elevation={0}>
                    <Typography className={classes.metricLabel}>
                        {i18n.t('tableMaps.details.tables')}
                    </Typography>
                    <Typography className={classes.metricValue}>{details.tables.length}</Typography>
                </Paper>
                <Paper className={classes.metricCard} elevation={0}>
                    <Typography className={classes.metricLabel}>
                        {i18n.t('tableMaps.details.totalSeats')}
                    </Typography>
                    <Typography className={classes.metricValue}>{totalSeats}</Typography>
                </Paper>
            </div>

            <div>
                <Typography className={classes.sectionLabel}>
                    {i18n.t('tableMaps.details.overviewSection')}
                </Typography>
                <div className={classes.previewWrapper}>
                    <div className={classes.summaryCardHeader}>
                        <Typography className={classes.sectionLabel}>
                            {i18n.t('tableMaps.wizard.review.tableMapPreview')}
                        </Typography>
                    </div>
                    <div className={classes.previewCanvas}>
                        <TableMapPreview draft={details} />
                    </div>
                </div>
            </div>

            <div className={classes.summaryCard}>
                <div className={classes.summaryCardHeader}>
                    <Typography className={classes.sectionLabel}>
                        {i18n.t('tableMaps.details.summary')}
                    </Typography>
                </div>
                <div className={classes.summaryRows}>
                    <div className={classes.summaryRow}>
                        <Typography className={classes.summaryLabel}>
                            {i18n.t('tableMaps.details.tableMapName')}
                        </Typography>
                        <Typography className={classes.summaryValue}>{details.name}</Typography>
                    </div>
                    <div className={classes.summaryRow}>
                        <Typography className={classes.summaryLabel}>
                            {i18n.t('tableMaps.details.tables')}
                        </Typography>
                        <Typography className={classes.summaryValue}>
                            {details.tables.length}
                        </Typography>
                    </div>
                    <div className={classes.summaryRow}>
                        <Typography className={classes.summaryLabel}>
                            {i18n.t('tableMaps.details.totalSeats')}
                        </Typography>
                        <Typography className={classes.summaryValue}>{totalSeats}</Typography>
                    </div>
                    <div className={classes.summaryRow}>
                        <Typography className={classes.summaryLabel}>
                            {i18n.t('tableMaps.details.activeTables')}
                        </Typography>
                        <Typography className={classes.summaryValue}>{activeTables}</Typography>
                    </div>
                    <div className={classes.summaryRow}>
                        <Typography className={classes.summaryLabel}>
                            {i18n.t('tableMaps.details.published')}
                        </Typography>
                        <Typography className={classes.summaryValue}>
                            {details.publishedAt ? formatPublishedAt(details.publishedAt) : '—'}
                        </Typography>
                    </div>
                </div>
            </div>
            {skeleton}
        </div>
    );
}
